import { cx } from '@emotion/css';
import type { ComponentProps, FC } from 'react';
import { Dialog, type DialogRootChangeEventDetails } from '@base-ui/react';

export interface ODSModalContainerProps extends Omit<ComponentProps<'div'>, 'autoFocus'> {
  /** Move focus to first tabbable element in modal on open */
  autoFocus?: ComponentProps<typeof Dialog.Popup>['initialFocus'];
  /**
   * Modal backdrop. `true` to render a backdrop. `false` to hide the backdrop.
   * `'static'` shows the backdrop and disables modal dismissal when clicking outside modal.
   * @default `true`
   * */
  backdrop?: boolean | 'static';
  /** Vertically center the modal */
  centered?: boolean;
  /** Whether the modal is open or closed */
  isOpen?: boolean;
  /** Callback fired when the modal is closed */
  onClosed?: () => void;
  /** Callback fired when the modal is opened */
  onOpened?: () => void;
  /** Make the modal body scrollable, rather than letting modal run off viewport */
  scrollable?: boolean;
  /**
   * Optional modal size modifier
   * @default 'md'
   * */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** @deprecated Use data-testid */
  testSelector?: string;
  /** Callback fired when the modal open state is changed */
  toggle?: (event: DialogRootChangeEventDetails['event']) => void;
  /**
   * Set zIndex of modal portal container.
   * */
  zIndex?: number;
}

export const ODSModalContainer: FC<ODSModalContainerProps> = ({
  autoFocus = true,
  backdrop = true,
  centered = false,
  children,
  className,
  isOpen = false,
  onClosed,
  onOpened,
  scrollable,
  size = 'md',
  testSelector = 'ods-modal',
  toggle,
  zIndex,
  ...componentProps
}) => {
  return (
    <Dialog.Root
      open={isOpen}
      disablePointerDismissal={backdrop === 'static'}
      onOpenChange={(open, details) => {
        toggle?.(details.event);
        if (open) {
          onOpened?.();
        } else {
          onClosed?.();
        }
      }}
    >
      <Dialog.Portal style={{ position: 'relative', zIndex }}>
        {backdrop && <Dialog.Backdrop className={cx('modal-backdrop show')} />}
        {/* `display: block` is necessary for bootstrap styles */}
        <Dialog.Viewport className="modal fade show" style={{ display: 'block' }}>
          <Dialog.Popup
            {...componentProps}
            initialFocus={autoFocus}
            className={cx(className, 'modal-dialog ods-modal-dialog', {
              [`modal-${size}`]: !!size && size !== 'md',
              'modal-dialog-centered': centered,
              'modal-dialog-scrollable': scrollable,
            })}
            data-testid={componentProps['data-testid'] ?? testSelector}
          >
            <div className={cx('modal-content ods-modal-container')}>{children}</div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
